export type DppCompletenessCheck = {
  key: string;
  label: string;
  complete: boolean;
  gap: string;
};

type DppCompletenessInput = {
  productName?: string | null;
  brandName?: string | null;
  skuModel?: string | null;
  categoryName?: string | null;
  categoryCode?: string | null;
  gtinEan?: string | null;
  imageUrl?: string | null;
  materialsCount: number;
  materialWeightPercentageTotal: number;
  includeConsumerPackaging?: boolean;
  packagingCount: number;
  expectedLifetimeYears?: number | string | null;
  repairabilityScore?: number | string | null;
  recyclingInstructions?: string | null;
  hasRepairManualDocument?: boolean;
  hasDisassemblyGuideDocument?: boolean;
  hasDopDocument?: boolean;
  isTextile: boolean;
  isBattery: boolean;
  isIndustrial: boolean;
  isConstruction?: boolean;
  isFurniture?: boolean;
  careSymbolsCount: number;
  microplasticsStatus?: string | null;
  materialsContainWoodOrLeather?: boolean;
  eudrDdsReference?: string | null;
  flameRetardantsPresent?: boolean;
  flameRetardantChemicalNamesCount?: number;
  ratedCapacityAh?: number | string | null;
  ratedEnergyKwh?: number | string | null;
  nominalVoltageV?: number | string | null;
  minimumVoltageV?: number | string | null;
  maximumVoltageV?: number | string | null;
  powerCapabilityLimits?: string | null;
  roundTripEfficiencyPercent?: number | string | null;
  cycleLifetimeDescriptor?: string | null;
  operatingTemperatureWindow?: string | null;
  extinguishingAgents?: string | null;
  safetyHandlingMeasures?: string | null;
  sparePartsAvailabilityYears?: number | string | null;
  softwareSupportYears?: number | string | null;
};

const hasText = (value: string | null | undefined) => Boolean(value?.trim());

const hasPositiveNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return value > 0;

  const trimmed = value.trim();
  if (!trimmed) return false;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0;
};

const hasNumericValue = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value);

  const trimmed = value.trim();
  if (!trimmed) return false;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed);
};

export function buildDppCompletenessChecks({
  productName,
  brandName,
  skuModel,
  categoryName,
  categoryCode,
  gtinEan,
  imageUrl,
  materialsCount,
  materialWeightPercentageTotal,
  includeConsumerPackaging,
  packagingCount,
  expectedLifetimeYears,
  repairabilityScore,
  recyclingInstructions,
  hasRepairManualDocument,
  hasDisassemblyGuideDocument,
  hasDopDocument,
  isTextile,
  isBattery,
  isIndustrial,
  isConstruction,
  isFurniture,
  careSymbolsCount,
  microplasticsStatus,
  materialsContainWoodOrLeather,
  eudrDdsReference,
  flameRetardantsPresent,
  flameRetardantChemicalNamesCount,
  ratedCapacityAh,
  ratedEnergyKwh,
  nominalVoltageV,
  minimumVoltageV,
  maximumVoltageV,
  powerCapabilityLimits,
  roundTripEfficiencyPercent,
  cycleLifetimeDescriptor,
  operatingTemperatureWindow,
  extinguishingAgents,
  safetyHandlingMeasures,
  sparePartsAvailabilityYears,
  softwareSupportYears,
}: DppCompletenessInput): DppCompletenessCheck[] {
  return [
    {
      key: 'identity-name',
      label: 'Product identity is defined',
      complete: hasText(productName) && hasText(brandName),
      gap: 'Add a product title and brand name.',
    },
    {
      key: 'identity-sku',
      label: 'SKU / model is present',
      complete: hasText(skuModel),
      gap: 'Add a SKU or model reference.',
    },
    {
      key: 'identity-category',
      label: 'Category is mapped',
      complete: hasText(categoryName) || hasText(categoryCode),
      gap: 'Assign a product category.',
    },
    {
      key: 'identity-gtin',
      label: 'GTIN / EAN is captured',
      complete: hasText(gtinEan),
      gap: 'Provide a GTIN / EAN for external traceability.',
    },
    {
      key: 'identity-image',
      label: 'Product image uploaded',
      complete: hasText(imageUrl),
      gap: 'Upload a product image for the passport header and public view.',
    },
    {
      key: 'materials',
      label: 'BOM / materials total 100% by weight',
      complete:
        materialsCount > 0 &&
        Math.abs(materialWeightPercentageTotal - 100) < 0.01,
      gap:
        materialsCount === 0
          ? 'Add bill-of-materials entries.'
          : `Complete the BOM to 100% by weight. Current total: ${materialWeightPercentageTotal.toFixed(2)}%.`,
    },
    {
      key: 'packaging',
      label:
        includeConsumerPackaging === false
          ? 'No-packaging declaration is recorded'
          : 'Packaging profile added',
      complete: includeConsumerPackaging === false || packagingCount > 0,
      gap: 'Add at least one packaging entry or mark that the item comes with no packaging.',
    },
    {
      key: 'circularity-core',
      label: 'Circularity core data is complete',
      complete:
        hasPositiveNumber(expectedLifetimeYears) &&
        hasNumericValue(repairabilityScore) &&
        hasText(recyclingInstructions),
      gap: 'Add expected lifetime, repairability score, and recycling instructions.',
    },
    ...(isTextile
      ? [
          {
            key: 'textile-care',
            label: 'Textile care symbols are provided',
            complete: careSymbolsCount > 0,
            gap: 'Add ISO care symbols for textile handling.',
          },
          {
            key: 'textile-microplastics',
            label: 'Microplastics status declared',
            complete: hasText(microplasticsStatus),
            gap: 'Declare the microplastics shedding status.',
          },
        ]
      : []),
    ...(isBattery
      ? [
          {
            key: 'battery-repair-manual',
            label: 'Battery repair manual is attached',
            complete: Boolean(hasRepairManualDocument),
            gap: 'Attach the repair manual required for batteries.',
          },
          {
            key: 'battery-disassembly-guide',
            label: 'Battery disassembly guide is attached',
            complete: Boolean(hasDisassemblyGuideDocument),
            gap: 'Attach the disassembly guide required for batteries.',
          },
          {
            key: 'battery-electrical',
            label: 'Battery electrical characteristics are complete',
            complete:
              hasPositiveNumber(ratedCapacityAh) &&
              hasPositiveNumber(ratedEnergyKwh) &&
              hasPositiveNumber(nominalVoltageV) &&
              hasPositiveNumber(minimumVoltageV) &&
              hasPositiveNumber(maximumVoltageV) &&
              hasText(powerCapabilityLimits),
            gap: 'Add rated capacity, rated energy, voltage range, and C-rate / power capability limits.',
          },
          {
            key: 'battery-durability',
            label: 'Battery durability metrics are complete',
            complete:
              hasNumericValue(roundTripEfficiencyPercent) &&
              hasText(cycleLifetimeDescriptor) &&
              hasText(operatingTemperatureWindow),
            gap: 'Add round-trip efficiency, cycle lifetime, and operating temperature window.',
          },
          {
            key: 'battery-safety',
            label: 'Battery safety handling data is complete',
            complete:
              hasText(extinguishingAgents) && hasText(safetyHandlingMeasures),
            gap: 'Add extinguishing agents and safety handling measures.',
          },
        ]
      : []),
    ...(categoryCode === 'ELECTRONICS_ICT'
      ? [
          {
            key: 'electronics-repair-manual',
            label: 'Electronics repair manual is attached',
            complete: Boolean(hasRepairManualDocument),
            gap: 'Attach the repair manual required for electronics.',
          },
          {
            key: 'electronics-disassembly-guide',
            label: 'Electronics disassembly guide is attached',
            complete: Boolean(hasDisassemblyGuideDocument),
            gap: 'Attach the disassembly instructions PDF required for electronics.',
          },
        ]
      : []),
    ...(isFurniture
      ? [
          {
            key: 'furniture-disassembly-guide',
            label: 'Furniture disassembly guide is attached',
            complete: Boolean(hasDisassemblyGuideDocument),
            gap: 'Attach the disassembly guide required for furniture.',
          },
        ]
      : []),
    ...(isIndustrial
      ? [
          {
            key: 'industrial-spare-parts',
            label: 'Industrial spare parts guarantee is captured',
            complete: hasPositiveNumber(sparePartsAvailabilityYears),
            gap: 'Add guaranteed spare parts availability years for industrial machinery.',
          },
          {
            key: 'industrial-software-support',
            label: 'Industrial software support commitment is captured',
            complete: hasPositiveNumber(softwareSupportYears),
            gap: 'Add guaranteed software support years for industrial machinery.',
          },
        ]
      : []),
    ...(isConstruction
      ? [
          {
            key: 'construction-dop',
            label: 'Construction DoP is attached',
            complete: Boolean(hasDopDocument),
            gap: 'Attach the Declaration of Performance (DoP) PDF for construction products.',
          },
        ]
      : []),
    ...(isFurniture && materialsContainWoodOrLeather
      ? [
          {
            key: 'furniture-eudr',
            label: 'Furniture EUDR DDS reference is captured',
            complete: hasText(eudrDdsReference),
            gap: 'Add the EUDR DDS reference because the material list includes wood or leather.',
          },
        ]
      : []),
    ...(isFurniture && flameRetardantsPresent
      ? [
          {
            key: 'furniture-flame-retardants',
            label: 'Flame retardant chemicals are declared',
            complete: (flameRetardantChemicalNamesCount ?? 0) > 0,
            gap: 'Declare at least one flame retardant chemical when flame retardants are present.',
          },
        ]
      : []),
  ];
}

export function calculateDppCompletenessScore(checks: DppCompletenessCheck[]) {
  const completedChecks = checks.filter((check) => check.complete).length;

  return {
    completedChecks,
    completenessScore: Math.round(
      (completedChecks / Math.max(checks.length, 1)) * 100,
    ),
    publicationGaps: checks
      .filter((check) => !check.complete)
      .map((check) => check.gap),
  };
}
